import { PricingGridImportProfile, PricingGridImportStatus, PricingType } from '../enums';

/**
 * Import de grilles tarifaires multi-transporteurs.
 *
 * Une grille consolidée (un fichier, N transporteurs, N départs, N
 * destinations) ressort en `tariff_versions` + `tariff_lanes` + `tariff_bands`,
 * après un mapping de colonnes puis deux rapprochements confirmés par
 * l'utilisateur : les transporteurs, puis les deux extrémités des lanes. Le
 * fichier n'est pas contraint : aucun template à respecter.
 *
 * Le point clé du modèle : **départ et arrivée se résolvent exactement de la
 * même manière**. Une extrémité est soit un lieu du carnet (`locations`), soit
 * une zone tarifaire (`tariff_zones`) — existante ou créée à la volée par
 * géocodage. Rien dans le pipeline ne distingue l'origine de la destination,
 * ce qui rend le profil réutilisable pour des grilles zone→zone, site→site ou
 * mixtes sans code supplémentaire.
 */

// ─── Extrémité de lane (départ OU arrivée, indifféremment) ─────────────────

/** Identité brute d'une extrémité, telle que lue dans le fichier. */
export interface GridEndpointRef {
  /** Libellé source (nom de lieu, ville, zone…) — jamais vide. */
  label: string;
  countryIso3: string;
  postalCode: string | null;
  /** Département/subdivision, quand la source le fournit (désambiguïsation). */
  department: string | null;
  /** Voie et numéro, quand la grille descend au niveau de l'adresse. */
  address: string | null;
}

/**
 * Politique de résolution d'un côté de la lane, fixée une fois pour tout
 * l'import.
 *
 * Les deux côtés d'une grille n'obéissent presque jamais à la même logique :
 * le départ est typiquement un site que l'organisation connaît, l'arrivée un
 * point client qu'elle ne connaît pas et qu'il faut situer géographiquement.
 * Sans ce réglage, chaque extrémité serait arbitrée séparément — deux cents
 * décisions pour exprimer une seule règle.
 */
export type GridEndpointPolicy =
  /** Uniquement des lieux du carnet. Ce qui n'y figure pas est écarté. */
  | 'LOCATION'
  /** Uniquement de la géographie : zone, code postal, département. */
  | 'GEO'
  /** Les deux, au mieux : lieu connu s'il existe, géographie sinon. */
  | 'BEST';

/** Côté de la lane auquel se rapporte une extrémité. */
export type GridEndpointSide = 'ORIGIN' | 'DEST';

/** Nature du référentiel derrière un candidat d'extrémité. */
export type GridEndpointKind = 'LOCATION' | 'ZONE';

export interface GridEndpointCandidate {
  id: string;
  kind: GridEndpointKind;
  label: string;
  /** Code interne (lieu) ou code de zone. */
  code: string | null;
  /** Contexte affichable : CP, ville, département couvert… */
  context: string | null;
  confidence: number;
}

/**
 * Nature retenue pour une extrémité — c'est-à-dire la granularité à laquelle
 * le tarif s'applique.
 *
 * Une extrémité de grille n'est pas toujours un point : selon les
 * conventions du transporteur, elle désigne un site précis, une commune, un
 * périmètre ou un département entier. Chacune de ces natures a son équivalent
 * exact dans les clés de lane du moteur de cotation, donc aucune n'oblige à
 * approximer.
 */
export type GridEndpointAction =
  /** Lieu du carnet d'adresses — le tarif part de ce site précis. */
  | 'MATCH_LOCATION'
  /** Zone tarifaire déjà définie (périmètre dessiné, liste de CP…). */
  | 'MATCH_ZONE'
  /** Créer une zone : périmètre circulaire autour du point géolocalisé. */
  | 'CREATE_ZONE'
  /** Code postal exact — la commune, sans créer de périmètre. */
  | 'USE_POSTAL_CODE'
  /** Département entier. */
  | 'USE_DEPARTMENT'
  | 'IGNORE';

export interface GridEndpointProposal {
  /** Clé stable du groupe — renvoyée telle quelle dans les décisions. */
  key: string;
  ref: GridEndpointRef;
  /**
   * Côté concerné. Un même libellé présent des deux côtés donne deux
   * propositions distinctes : les politiques de départ et d'arrivée diffèrent,
   * donc les décisions aussi.
   */
  side: GridEndpointSide;
  /** Nombre de lignes du fichier portant cette extrémité. */
  occurrences: number;
  /** Vrai si l'extrémité apparaît au moins une fois en départ. */
  usedAsOrigin: boolean;
  /** Vrai si l'extrémité apparaît au moins une fois en arrivée. */
  usedAsDest: boolean;
  candidates: GridEndpointCandidate[];
  suggestedAction: GridEndpointAction;
  /**
   * Natures réellement disponibles pour cette extrémité : le code postal exact
   * n'est proposé que si le fichier en fournit un, le département que s'il est
   * connu ou déductible. Proposer une option inapplicable ne ferait que
   * produire des lignes écartées au commit.
   */
  availableActions: GridEndpointAction[];
  /** Code postal retenu si l'utilisateur choisit USE_POSTAL_CODE. */
  postalCode: string | null;
  /** Département retenu si l'utilisateur choisit USE_DEPARTMENT. */
  department: string | null;
}

export interface GridEndpointDecision {
  key: string;
  action: GridEndpointAction;
  /** Id du lieu ou de la zone visé (actions MATCH_*). */
  targetId?: string;
  /** Code de zone souhaité (action CREATE_ZONE) — sinon dérivé du libellé. */
  zoneCode?: string;
  /**
   * Code postal retenu, quand il ne vient pas du fichier (action
   * USE_POSTAL_CODE).
   *
   * Trois chemins mènent au même endroit : reprendre le CP du lieu rapproché,
   * le faire dire par le géocodeur, ou le saisir. Les deux derniers n'ont
   * aucune trace dans le classeur — sans ce champ, une extrémité que le
   * fichier ne code pas ne pourrait être résolue qu'en créant une zone.
   */
  postalCode?: string;
}

// ─── Transporteurs ─────────────────────────────────────────────────────────

export type GridCarrierAction = 'MATCH' | 'CREATE' | 'IGNORE';

export interface GridCarrierCandidate {
  id: string;
  label: string;
  code: string | null;
  confidence: number;
}

export interface GridCarrierProposal {
  key: string;
  /** Libellé source, tel quel (ex. « 010 BARNIER 28t »). */
  rawValue: string;
  /** Libellé nettoyé : code de tête et tonnage retirés (ex. « BARNIER »). */
  cleanedName: string;
  /** Code numérique de tête détecté dans le libellé (ex. « 010 »). */
  detectedCode: string | null;
  /** Tonnage lu dans le libellé (ex. 28) — devient la borne haute de tranche. */
  detectedMaxQty: number | null;
  occurrences: number;
  candidates: GridCarrierCandidate[];
  suggestedAction: GridCarrierAction;
}

export interface GridCarrierDecision {
  key: string;
  action: GridCarrierAction;
  targetId?: string;
}

// ─── Lignes parsées ────────────────────────────────────────────────────────

/** Coûts annexes portés par une ligne (constants par transporteur en pratique). */
export interface GridAncillaryValues {
  /** Retour palette vide, € par palette. */
  rpvPerPallet: number | null;
  /** Attente au-delà de la franchise, € par heure. */
  waitingPerHour: number | null;
  /** Plus-value chantier / site difficile, € forfaitaires. */
  siteSurcharge: number | null;
  /** Mois de référence de l'indexation gazole, tel quel (ex. « jan 2023 »). */
  fuelIndexRef: string | null;
}

export interface ParsedGridRow {
  /** Numéro de ligne dans la feuille source (1-based, en-tête comprise). */
  rowIndex: number;
  carrierLabel: string;
  origin: GridEndpointRef;
  dest: GridEndpointRef;
  price: number | null;
  minQty: number | null;
  maxQty: number | null;
  unit: string | null;
  pricingType: PricingType | null;
  ancillary: GridAncillaryValues;
  note: string | null;
  /** Erreurs bloquantes : la ligne ne sera pas commitée. */
  errors: string[];
}

export interface GridParseResult {
  rows: ParsedGridRow[];
  /** Colonnes reconnues → en-tête source, pour affichage. */
  recognizedColumns: Record<string, string>;
  /** En-têtes du fichier qu'aucun champ du profil ne réclame. */
  ignoredColumns: string[];
  validRowCount: number;
  errorRowCount: number;
}

// ─── Mapping de colonnes ───────────────────────────────────────────────────

/**
 * Champs canoniques auxquels une colonne du fichier peut être affectée.
 *
 * Le fichier de l'utilisateur n'est pas contraint : c'est le mapping qui
 * l'explique au système. Un import n'exige donc aucun reformatage préalable.
 */
export type GridField =
  | 'carrier'
  | 'originLabel'
  | 'originAddress'
  | 'originCountry'
  | 'originPostal'
  | 'originDepartment'
  | 'destLabel'
  | 'destAddress'
  | 'destCountry'
  | 'destPostal'
  | 'destDepartment'
  | 'price'
  | 'minQty'
  | 'maxQty'
  | 'unit'
  | 'pricingType'
  | 'rpvPerPallet'
  | 'waitingPerHour'
  | 'siteSurcharge'
  | 'fuelIndexRef'
  | 'note';

export interface GridFieldSpec {
  key: GridField;
  label: string;
  /** Sans ce champ, une ligne du cas d'usage courant est inexploitable. */
  required: boolean;
  /** Regroupement d'affichage dans le sélecteur de colonnes. */
  group: 'Transporteur' | 'Départ' | 'Arrivée' | 'Tarif' | 'Coûts annexes' | 'Divers';
  hint?: string;
}

/** Catalogue de tous les champs connus, indépendamment du cas d'usage. */
const FIELD_CATALOG: Record<GridField, Omit<GridFieldSpec, 'required'>> = {
  carrier: {
    key: 'carrier', label: 'Transporteur', group: 'Transporteur',
    hint: 'Le code de tête et le tonnage collés au nom sont détectés automatiquement.',
  },
  originLabel: {
    key: 'originLabel', label: 'Départ — lieu, ville ou zone', group: 'Départ',
    hint: 'Ce que désigne la colonne : un lieu du carnet, une commune, une zone, un département. La nature se choisit ensuite, extrémité par extrémité. Colonne non affectée = tarif valable au départ de partout.',
  },
  originAddress: {
    key: 'originAddress', label: 'Départ — adresse', group: 'Départ',
    hint: 'Voie et numéro. Affine la géolocalisation quand la grille descend à l’adresse.',
  },
  originCountry: { key: 'originCountry', label: 'Départ — pays', group: 'Départ' },
  originPostal: { key: 'originPostal', label: 'Départ — code postal', group: 'Départ' },
  originDepartment: { key: 'originDepartment', label: 'Départ — département', group: 'Départ' },
  destLabel: {
    key: 'destLabel', label: 'Arrivée — lieu, ville ou zone', group: 'Arrivée',
    hint: 'Même traitement que le départ. Colonne non affectée = tarif valable vers toutes destinations.',
  },
  destAddress: {
    key: 'destAddress', label: 'Arrivée — adresse', group: 'Arrivée',
    hint: 'Voie et numéro. Affine la géolocalisation quand la grille descend à l’adresse.',
  },
  destCountry: { key: 'destCountry', label: 'Arrivée — pays', group: 'Arrivée' },
  destPostal: { key: 'destPostal', label: 'Arrivée — code postal', group: 'Arrivée' },
  destDepartment: {
    key: 'destDepartment', label: 'Arrivée — département', group: 'Arrivée',
    hint: 'Lève les ambiguïtés de géolocalisation entre communes homonymes.',
  },
  price: { key: 'price', label: 'Prix', group: 'Tarif' },
  minQty: { key: 'minQty', label: 'Tranche — de', group: 'Tarif' },
  maxQty: { key: 'maxQty', label: 'Tranche — à', group: 'Tarif' },
  unit: { key: 'unit', label: 'Unité', group: 'Tarif' },
  pricingType: {
    key: 'pricingType', label: 'Type de prix', group: 'Tarif',
    hint: 'FLAT (forfait sur la tranche) ou PER_UNIT (prix unitaire).',
  },
  rpvPerPallet: { key: 'rpvPerPallet', label: 'Retour palette (€/pal)', group: 'Coûts annexes' },
  waitingPerHour: { key: 'waitingPerHour', label: 'Attente (€/h)', group: 'Coûts annexes' },
  siteSurcharge: { key: 'siteSurcharge', label: 'PV chantier (€)', group: 'Coûts annexes' },
  fuelIndexRef: { key: 'fuelIndexRef', label: 'Indexation gazole', group: 'Coûts annexes' },
  note: { key: 'note', label: 'Note', group: 'Divers' },
};

function fields(required: GridField[], optional: GridField[]): GridFieldSpec[] {
  return [
    ...required.map((k) => ({ ...FIELD_CATALOG[k], required: true })),
    ...optional.map((k) => ({ ...FIELD_CATALOG[k], required: false })),
  ];
}

/** Champs de coûts annexes — communs à tous les cas d'usage. */
const ANCILLARY_FIELDS: GridField[] = [
  'rpvPerPallet',
  'waitingPerHour',
  'siteSurcharge',
  'fuelIndexRef',
];

const LANE_OPTIONAL_FIELDS: GridField[] = [
  'originAddress',
  'originCountry',
  'originPostal',
  'originDepartment',
  'destAddress',
  'destCountry',
  'destPostal',
  'destDepartment',
  ...ANCILLARY_FIELDS,
  'note',
];

/**
 * Ce qu'un cas d'usage change : les champs proposés au mapping, les étapes du
 * parcours, et ce que le commit écrit.
 */
export interface GridProfileSpec {
  key: PricingGridImportProfile;
  label: string;
  description: string;
  fields: GridFieldSpec[];
  /** L'import produit des lanes — donc réclame l'étape « extrémités ». */
  createsLanes: boolean;
  /**
   * Plusieurs lignes portant la même lane forment plusieurs tranches au lieu
   * d'être des doublons.
   */
  multiBand: boolean;
  /**
   * Au moins une colonne de coût annexe doit être mappée (cas d'usage dont
   * c'est l'unique objet).
   */
  requiresAnyAncillary: boolean;
  defaults: Partial<PricingGridImportParams>;
}

export const GRID_PROFILES: Record<PricingGridImportProfile, GridProfileSpec> = {
  [PricingGridImportProfile.FULL_TRUCK]: {
    key: PricingGridImportProfile.FULL_TRUCK,
    label: 'Tarifs camion complet',
    description:
      'Un prix par transporteur, départ et destination. Une seule tranche (le voyage), ' +
      'éventuellement bornée par le tonnage du véhicule.',
    fields: fields(
      ['carrier', 'price'],
      ['originLabel', 'destLabel', ...LANE_OPTIONAL_FIELDS, 'minQty', 'maxQty', 'unit', 'pricingType'],
    ),
    createsLanes: true,
    multiBand: false,
    requiresAnyAncillary: false,
    defaults: { unit: 't', minQty: 0, maxQty: 30, pricingType: PricingType.FLAT },
  },
  [PricingGridImportProfile.BANDED]: {
    key: PricingGridImportProfile.BANDED,
    label: 'Tarifs par tranche',
    description:
      'Plusieurs lignes par relation, chacune portant une tranche (nombre de palettes, ' +
      'poids, mètres plancher). Les tranches se cumulent au lieu de se concurrencer.',
    fields: fields(
      ['carrier', 'price', 'maxQty'],
      ['originLabel', 'destLabel', ...LANE_OPTIONAL_FIELDS, 'minQty', 'unit', 'pricingType'],
    ),
    createsLanes: true,
    multiBand: true,
    requiresAnyAncillary: false,
    defaults: { unit: 'pal', minQty: 0, maxQty: null, pricingType: PricingType.FLAT },
  },
  [PricingGridImportProfile.ANCILLARY]: {
    key: PricingGridImportProfile.ANCILLARY,
    label: 'Coûts annexes',
    description:
      'Ni tarif ni zone : uniquement les montants annexes par transporteur ' +
      '(retour palette, attente, plus-value chantier, indexation gazole).',
    fields: fields(['carrier'], [...ANCILLARY_FIELDS, 'originLabel', 'note']),
    createsLanes: false,
    multiBand: false,
    requiresAnyAncillary: true,
    defaults: {},
  },
};

export const GRID_PROFILE_LIST: GridProfileSpec[] = Object.values(GRID_PROFILES);

export function gridFieldsFor(profile: PricingGridImportProfile): GridFieldSpec[] {
  return (GRID_PROFILES[profile] ?? GRID_PROFILES[PricingGridImportProfile.FULL_TRUCK]).fields;
}

export function gridRequiredFieldsFor(profile: PricingGridImportProfile): GridField[] {
  return gridFieldsFor(profile).filter((f) => f.required).map((f) => f.key);
}

/** Champs de coûts annexes présents dans un mapping donné. */
export function mappedAncillaryFields(columns: Partial<Record<GridField, number>>): GridField[] {
  return ANCILLARY_FIELDS.filter((f) => columns[f] !== undefined);
}

/**
 * Comment lire l'onglet sélectionné : où commence le tableau, et quelle
 * colonne alimente quel champ.
 */
export interface GridColumnMapping {
  /** Index (0-based) de la ligne d'en-tête dans la feuille. */
  headerRowIndex: number;
  /** Champ canonique → index de colonne. Champ absent = non fourni. */
  columns: Partial<Record<GridField, number>>;
}

/** Onglet du classeur, tel que proposé au choix de l'utilisateur. */
export interface GridImportSheet {
  name: string;
  rowCount: number;
  columnCount: number;
}

/**
 * État de l'étape « colonnes » : tout ce qu'il faut pour afficher l'aperçu du
 * fichier et laisser l'utilisateur corriger la lecture proposée.
 */
export interface GridMappingState {
  sheets: GridImportSheet[];
  selectedSheet: string;
  mapping: GridColumnMapping;
  /** Ligne d'en-tête retenue, telle quelle. */
  headers: string[];
  /** Premières lignes de données, pour juger de la lecture. */
  sampleRows: unknown[][];
  /** Champs remplis depuis la mémoire des mappings du tenant. */
  rememberedFields: GridField[];
  /**
   * Pourquoi la lecture a échoué avec ce mapping, le cas échéant. L'étape
   * « colonnes » est celle où l'on corrige une mauvaise lecture : elle doit
   * afficher l'erreur, jamais refuser de s'afficher.
   */
  parseError: string | null;
}

// ─── Paramètres d'import ───────────────────────────────────────────────────

export interface PricingGridImportParams {
  /** Début de validité des versions créées (ISO yyyy-mm-dd). */
  validFrom: string;
  validTo: string | null;
  /** Nom donné aux versions créées (ex. « Grille complet 2026 »). */
  versionName: string;
  /** ACTIVE au commit, sinon DRAFT (revue avant mise en production). */
  activate: boolean;
  /** Unité des tranches créées (ex. « t »). */
  unit: string;
  /** Borne basse de tranche (défaut 0). */
  minQty: number;
  /** Borne haute par défaut, quand le libellé transporteur n'en porte pas. */
  maxQty: number | null;
  /** Prix fixe sur la tranche, ou prix unitaire. */
  pricingType: PricingType;
  /** Rayon maximal des zones créées, en km. */
  radiusKm: number;
  /**
   * Réduit le rayon d'une zone à la moitié de la distance au voisin le plus
   * proche. Empêche deux zones créées dans le même import de se recouvrir.
   */
  adaptiveRadius: boolean;
  /** Pays par défaut quand la colonne est absente. */
  defaultCountryIso3: string;
  /** Comment résoudre les points de départ (cf. GridEndpointPolicy). */
  originPolicy: GridEndpointPolicy;
  /** Comment résoudre les points d'arrivée. */
  destPolicy: GridEndpointPolicy;
  /** Crée aussi les règles de coûts annexes lues dans le fichier. */
  importAncillaries: boolean;
}

// ─── Job ───────────────────────────────────────────────────────────────────

export interface IPricingGridImportJob {
  id: string;
  companyId: string | null;
  name: string;
  profile: PricingGridImportProfile;
  status: PricingGridImportStatus;
  sourceFileName: string | null;
  sourceSheetName: string | null;
  params: PricingGridImportParams;
  mapping: GridColumnMapping | null;
  sheets: GridImportSheet[];
  rowCount: number;
  errorCount: number;
  parseResult: Omit<GridParseResult, 'rows'> | null;
  result: PricingGridCommitResult | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PricingGridMatchesResponse {
  carriers: GridCarrierProposal[];
  endpoints: GridEndpointProposal[];
}

/** Ce qu'un commit a produit, et ce qu'il a écarté — et pourquoi. */
export interface PricingGridCommitResult {
  createdCarriers: number;
  createdZones: number;
  reusedZones: number;
  createdVersions: number;
  createdLanes: number;
  createdBands: number;
  createdAncillaryRules: number;
  skippedRows: number;
  /** Motifs d'exclusion agrégés (message → nombre de lignes). */
  skippedReasons: Record<string, number>;
  /**
   * Anomalies non bloquantes : l'import a bien eu lieu, mais un arbitrage a été
   * fait qui mérite d'être connu (valeurs divergentes, tranche déduite…).
   */
  warnings: Record<string, number>;
  /** Versions créées, pour rebond direct vers la fiche transporteur. */
  versions: { carrierId: string; carrierName: string; versionId: string; laneCount: number }[];
}

/** Aperçu avant commit : ce qui serait écrit, sans rien écrire. */
export interface PricingGridPreview {
  carriers: { carrierName: string; laneCount: number; sampleLanes: PricingGridPreviewLane[] }[];
  zonesToCreate: { code: string; label: string; radiusKm: number; postalCode: string | null }[];
  /** Règles de coûts annexes qui seraient créées, par transporteur. */
  ancillaryRules: { carrierName: string; rules: { name: string; detail: string }[] }[];
  skippedRows: number;
  skippedReasons: Record<string, number>;
  warnings: Record<string, number>;
  totalLanes: number;
}

export interface PricingGridPreviewLane {
  originKeyType: string;
  originKey: string;
  originLabel: string;
  destKeyType: string;
  destKey: string;
  destLabel: string;
  price: number;
  minQty: number;
  maxQty: number | null;
  unit: string;
}
